// Unpublished Work © 2024 Deere & Company.
import LoadingIcon from 'Ui/components/common/loading-icon';
import Box from '@mui/material/Box';
import {FilterChipLayout, SearchField, DataGrid, withDebounce} from '@deere/isg.component-library';
import React from 'react';
import SearchFilter from 'Ui/components/higher-order-components/search-filter';
import PropTypes from 'Utils/prop-type-utils';

function applyFilter(rows, filterKey, filterValue) {
    if (filterKey === 'date') {
        if (!filterValue[0] || !filterValue[1]) {
            return rows;
        }

        return rows.filter((row) => filterValue[0] <= new Date(row[filterKey]) && filterValue[1] >= new Date(row[filterKey]));
    }

    if (filterValue.length === 0) {
        return rows;
    }

    return rows.filter((row) => filterValue.includes(row[filterKey]));
}

function filterRows(rows, appliedFilters) {
    let filteredRows = rows;

    if (appliedFilters) {
        Object.keys(appliedFilters).forEach((appliedFilterKey) => {
            filteredRows = applyFilter(filteredRows, appliedFilterKey, appliedFilters[appliedFilterKey]);
        });
    }

    return filteredRows;
}

function ComponentLibraryDataTableWrapper(props) {
    const {
        appliedFilters,
        applySearch,
        columns,
        filters,
        initialState,
        loading,
        onSearchChange,
        rows,
        search,
        searchFields,
        setFilteredData,
        slotProps,
        translations
    } = props;

    if (loading) {
        return (
            <LoadingIcon
                className='table-loading-icon'
                size='50px'
            />
        );
    }

    if (rows.length === 0) {
        return (
            <h2 className='table-no-data'>
                {translations.NO_LOADED_DATA_AVAILABLE}
            </h2>
        );
    }

    const DebouncedSearchField = React.useMemo(() => withDebounce(SearchField), []);

    const filteredAndSearchedRows = React.useMemo(() => {
        const filteredRows = filterRows(rows, appliedFilters);

        const filteredAndSearchedRows = applySearch(filteredRows, searchFields);

        if (setFilteredData) {
            setFilteredData(filteredAndSearchedRows);
        }

        return filteredAndSearchedRows;
    }, [rows, appliedFilters, search]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                flex: '1 1 auto',
                minHeight: 0,
                overflow: 'hidden',
                maxHeight: 'calc(100dvh - 224px)'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                }}
            >
                <DebouncedSearchField
                    debounceTimeout={500}
                    onChange={onSearchChange}
                    placeholder={translations.SEARCH}
                    value={search}
                />
                <FilterChipLayout
                    className='table-filters'
                    hideMoreFilters={true}
                    hideSearchField={true}
                >
                    {filters}
                </FilterChipLayout>
            </Box>
            {
                filteredAndSearchedRows.length === 0 ?
                    <h2 className='table-no-data'>
                        {translations.NO_LOADED_DATA_AVAILABLE}
                    </h2> :
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: '1 1 auto',
                            minHeight: 0
                        }}
                    >
                        <DataGrid
                            columns={columns}
                            initialState={initialState}
                            rows={filteredAndSearchedRows}
                            slotProps={slotProps}
                            sx={{
                                height: '100%'
                            }}
                        />
                    </Box>
            }
        </Box>
    );
}

ComponentLibraryDataTableWrapper.propTypes = {
    appliedFilters: PropTypes.object,
    applySearch: PropTypes.func,
    columns: PropTypes.arrayOf(PropTypes.object),
    filters: PropTypes.element,
    initialState: PropTypes.object,
    loading: PropTypes.bool,
    onSearchChange: PropTypes.func,
    rows: PropTypes.arrayOf(PropTypes.object),
    search: PropTypes.string,
    searchFields: PropTypes.arrayOf(PropTypes.string),
    setFilteredData: PropTypes.func,
    slotProps: PropTypes.object,
    translations: PropTypes.translations
};

export default SearchFilter(ComponentLibraryDataTableWrapper);
